import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  /** @type {Record<string, string> | null} */
  #discountMap = null;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * 同商品切变体：只切到已有媒体，并立刻换该变体的折扣角标。
   * @param {string | number | undefined | null} mediaId
   * @param {string | number | undefined | null} [variantId]
   * @returns {boolean}
   */
  previewMedia(mediaId, variantId) {
    this.applyDiscountFlag(variantId);
    return this.#showMedia(mediaId);
  }

  /**
   * 按变体 ID 换成预渲染好的角标；该变体没折扣就藏起来。
   * @param {string | number | undefined | null} variantId
   * @returns {boolean}
   */
  applyDiscountFlag(variantId) {
    const host = this.querySelector('[data-ug-gallery-discount-flag]');
    if (!(host instanceof HTMLElement) || variantId == null || variantId === '') return false;
    const map = this.#getDiscountMap();
    const html = map[String(variantId)];
    if (html === undefined) return false;
    host.innerHTML = html;
    host.hidden = !String(html).trim();
    return true;
  }

  #getDiscountMap() {
    if (this.#discountMap) return this.#discountMap;
    const script = this.querySelector('[data-ug-gallery-discount-variants]');
    try {
      this.#discountMap = JSON.parse(script?.textContent || '{}');
    } catch {
      this.#discountMap = {};
    }
    return this.#discountMap;
  }

  /**
   * @param {string | number | undefined | null} mediaId
   * @returns {boolean}
   */
  #showMedia(mediaId) {
    if (mediaId == null || mediaId === '') return false;
    const id = String(mediaId);
    const slideshow = this.slideshow;
    const slideshowVisible = Boolean(
      slideshow && slideshow.offsetParent !== null && this.dataset.presentation !== 'grid'
    );

    if (slideshowVisible && slideshow.refs?.slides?.length) {
      const slide = slideshow.refs.slides.find((item) => String(item.getAttribute('slide-id')) === id);
      if (!slide) return false;
      slideshow.select({ id }, undefined, { animate: false });
      return true;
    }

    const escaped = typeof CSS !== 'undefined' && typeof CSS.escape === 'function' ? CSS.escape(id) : id;
    const tile = this.querySelector(`[data-media-id="${escaped}"]`);
    if (!(tile instanceof HTMLElement)) return false;
    if (this.dataset.presentation === 'grid') {
      tile.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      return true;
    }
    // slides 还没就绪时不能当成功，交给 replaceWith
    return false;
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;
    if (!source) return;

    const mustReplace = Boolean(event.detail.data.newProduct) || this.hasAttribute('data-hide-variants');
    if (!mustReplace) {
      if (!this.applyDiscountFlag(event.detail.resource?.id)) {
        this.#syncDiscountFlag(source);
      }
      const mediaId = event.detail.resource?.featured_media?.id;
      if (this.#showMedia(mediaId) || mediaId == null) return;
    }

    const newMediaGallery = source.querySelector('media-gallery');
    if (!newMediaGallery) return;
    this.replaceWith(newMediaGallery);
  };

  /**
   * 用 section 新 HTML 里的角标替换当前视口角标（金额/百分比随变体变）。
   * @param {Document | Element} source
   */
  #syncDiscountFlag(source) {
    const nextGallery = source.querySelector('media-gallery');
    const next = nextGallery?.querySelector('[data-ug-gallery-discount-flag]');
    const current = this.querySelector('[data-ug-gallery-discount-flag]');

    if (current && next) {
      current.replaceWith(next.cloneNode(true));
      return;
    }
    if (current && !next) {
      current.remove();
      return;
    }
    if (!current && next) {
      this.insertBefore(next.cloneNode(true), this.firstChild);
    }
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
